<script lang="ts">
  // Import past Garmin Connect activities: ask Garmin for a date range of up to 90 days. Garmin sends
  // them when it has them ready, and the athlete is notified when they arrive.
  import { createEventDispatcher, onDestroy } from 'svelte'
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import { requestGarminActivities } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError } from '@/Util/notify'

  export let backfillUrl: string
  /** How long recent requests took, when the server knows. */
  export let expectedMinutes: number | null = null
  /** When a request still on its way was made (unix seconds). */
  export let pendingSince: number | null = null

  const dispatch = createEventDispatcher<{ historicalData: void }>()

  // Garmin takes up to 90 days in one request; history goes back three years here.
  const MAX_DAYS = 90
  const COOLDOWN_SECONDS = 60
  const DAY = 86400000
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const parse = (s: string) => new Date(`${s}T00:00:00`)
  const shift = (s: string, days: number) => {
    const d = parse(s)
    d.setDate(d.getDate() + days)
    return iso(d)
  }
  const today = new Date()
  const minDate = iso(new Date(today.getTime() - 366 * 3 * DAY))
  const maxDate = iso(today)

  const PRESETS = [
    { id: '30', label: 'Last 30 days', days: 30 },
    { id: '60', label: 'Last 60 days', days: 60 },
    { id: '90', label: 'Last 90 days', days: 90 },
  ] as const
  type PresetId = (typeof PRESETS)[number]['id'] | 'custom'

  let preset: PresetId = '90'
  let from = shift(maxDate, -(MAX_DAYS - 1))
  let to = maxDate

  function choose(id: PresetId) {
    preset = id
    const p = PRESETS.find((p) => p.id === id)
    if (p) [from, to] = [shift(maxDate, -(p.days - 1)), maxDate]
  }

  // Keep the range within Garmin's 90 days by moving the other end.
  function customFrom(value: string) {
    preset = 'custom'
    from = value < minDate ? minDate : value > maxDate ? maxDate : value
    if (to < from) to = from
    if (to > shift(from, MAX_DAYS - 1)) to = shift(from, MAX_DAYS - 1)
  }
  function customTo(value: string) {
    preset = 'custom'
    to = value > maxDate ? maxDate : value < minDate ? minDate : value
    if (from > to) from = to
    if (from < shift(to, -(MAX_DAYS - 1))) from = shift(to, -(MAX_DAYS - 1))
  }

  const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  $: days = Math.round((parse(to).getTime() - parse(from).getTime()) / DAY) + 1
  $: rangeText = `${dateFormat.format(parse(from))} to ${dateFormat.format(parse(to))} (${days} ${days === 1 ? 'day' : 'days'})`

  let requesting = false
  let requested = ''
  let cooldown = 0
  let timer: ReturnType<typeof setInterval> | undefined
  onDestroy(() => clearInterval(timer))

  async function request() {
    requesting = true
    // Said as asked: the dates can change while the request is on its way.
    const asked = rangeText
    const start = parse(from).getTime()
    // The end of the last day is the next local midnight, which isn't 24 hours away on a daylight saving change.
    const end = Math.min(parse(shift(to, 1)).getTime() - 1, Date.now())
    const res = await requestGarminActivities(backfillUrl, start, end)
    requesting = false
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.GARMIN_IMPORT_REQUEST_FAILED, message: 'Could not ask Garmin for your activities. Please try again.' })
      return
    }
    requested = asked
    pendingSince = null
    cooldown = COOLDOWN_SECONDS
    clearInterval(timer)
    timer = setInterval(() => {
      if (--cooldown <= 0) clearInterval(timer)
    }, 1000)
  }

  function ago(seconds: number) {
    const minutes = Math.round((Date.now() / 1000 - seconds) / 60)
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
    return minutes < 60 ? rtf.format(-minutes, 'minute') : rtf.format(-Math.round(minutes / 60), 'hour')
  }
</script>

<Card.Root>
  <Card.Header class="gap-1 pb-3">
    <Card.Title class="text-base">Import past activities</Card.Title>
    <Card.Description>
      Ask Garmin for the activities from before you connected. Garmin sends them when it has them ready, usually within a few
      minutes and sometimes much longer.{#if expectedMinutes}{' '}Recent requests took about {expectedMinutes} {expectedMinutes === 1 ? 'minute' : 'minutes'}.{/if}
    </Card.Description>
  </Card.Header>
  <Card.Content class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Date range">
      {#each PRESETS as p (p.id)}
        <button type="button" role="radio" aria-checked={preset === p.id} on:click={() => choose(p.id)}
          class="h-8 cursor-pointer rounded-full border px-3 text-xs font-medium transition-colors
            {preset === p.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-accent'}">
          {p.label}
        </button>
      {/each}
      <button type="button" role="radio" aria-checked={preset === 'custom'} on:click={() => (preset = 'custom')}
        class="h-8 cursor-pointer rounded-full border px-3 text-xs font-medium transition-colors
          {preset === 'custom' ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-accent'}">
        Custom
      </button>
    </div>

    <div class="flex items-end gap-2">
      <label class="m-0 flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        From
        <input type="date" value={from} min={minDate} max={maxDate} on:change={(e) => customFrom(e.currentTarget.value)}
          class="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground" />
      </label>
      <label class="m-0 flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        To
        <input type="date" value={to} min={minDate} max={maxDate} on:change={(e) => customTo(e.currentTarget.value)}
          class="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground" />
      </label>
    </div>

    <div class="flex min-h-[52px] flex-col gap-2 rounded-lg bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
      <span class="text-sm text-muted-foreground" aria-live="polite">{rangeText}. Up to {MAX_DAYS} days in one request.</span>
      <Button on:click={request} disabled={requesting || cooldown > 0} class="sm:min-w-[180px]">
        {#if requesting}
          <i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />Requesting...
        {:else if cooldown > 0}
          Request again in {cooldown} s
        {:else}
          <i class="fa-solid fa-cloud-arrow-down mr-2" aria-hidden="true" />Request activities
        {/if}
      </Button>
    </div>

    {#if requested}
      <div role="status" class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
        <i class="fa-solid fa-circle-check mt-0.5" aria-hidden="true" />
        <span>Garmin was asked for {requested}. You can leave this page: you'll be notified when the activities arrive.</span>
      </div>
    {:else if pendingSince}
      <div role="status" class="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
        <i class="fa-solid fa-clock mt-0.5 text-muted-foreground" aria-hidden="true" />
        <span>A request is on its way: it was sent {ago(pendingSince)}. Garmin sends the activities when they're ready.</span>
      </div>
    {/if}

    <p class="m-0 text-xs text-muted-foreground">
      If Garmin turns the request down, <strong>Historical Data</strong> is probably not switched on for Xert.
      <button type="button" class="cursor-pointer border-0 bg-transparent p-0 font-medium text-primary underline-offset-2 hover:underline" on:click={() => dispatch('historicalData')}>Show me how to switch it on</button>
    </p>
  </Card.Content>
</Card.Root>
